// Right-column checkout panel. When VITE_STRIPE_PUBLISHABLE_KEY is set AND
// /api/create-checkout returns a clientSecret, this renders the real Stripe
// EmbeddedCheckout iframe. Otherwise it renders a stylish "secure checkout"
// placeholder that runs the existing /api/checkout-intent flow.
//
// The placeholder is intentionally NOT a fake card-input UI — it's an order
// summary + a single "complete checkout" CTA. Switching to real Stripe is a
// drop-in: the iframe replaces this panel entirely.

import { useEffect, useRef, useState } from 'react';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import type { Plan } from '../data/plans';

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as
  | string
  | undefined;

let stripePromise: Promise<Stripe | null> | null = null;
function getStripe(): Promise<Stripe | null> {
  if (!STRIPE_PUBLISHABLE_KEY) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  return stripePromise;
}

interface Props {
  plan: Plan;
  /** Cents — 0 if no add-on selected. */
  addOnCents: number;
  /** 'recruiter' | 'coach' | null — sent to backend for line-item context. */
  addOnFlavor: 'recruiter' | 'coach' | null;
  /** Total in dollars including add-on, used by the placeholder summary. */
  totalDollars: number;
  /** Called when the user completes the placeholder flow (no Stripe). */
  onPlaceholderConfirm: () => void;
  submitting: boolean;
  error: string | null;
  /** Quiz session ID — sent to create-checkout so the server can pre-fill customer_email. */
  clientSessionId: string | null;
  /** Override the checkout API endpoint. Defaults to /api/create-checkout. */
  checkoutEndpoint?: string;
}

export function StripeCheckoutPanel(props: Props) {
  // No publishable key → placeholder mode (writes intent + advances on click).
  if (!STRIPE_PUBLISHABLE_KEY) {
    return <PlaceholderPanel {...props} />;
  }
  // Key set → real Embedded Checkout. Re-key on plan/add-on/endpoint changes
  // so the iframe rebuilds with the new line items rather than trying to
  // update an existing session.
  const endpointKey = props.checkoutEndpoint ?? 'default';
  return (
    <EmbeddedPanel
      key={`${props.plan.id}:${props.addOnFlavor ?? 'none'}:${props.addOnCents}:${props.clientSessionId ?? ''}:${endpointKey}`}
      {...props}
    />
  );
}

// ---------- Placeholder ----------

function PlaceholderPanel({
  plan,
  addOnCents,
  totalDollars,
  onPlaceholderConfirm,
  submitting,
  error,
}: Props) {
  const fmt = (n: number) =>
    '$' + n.toFixed(2).replace(/\.00$/, n % 1 === 0 ? '.00' : '');
  const planAmount =
    parseFloat(plan.monthlyEquivalent.replace(/[^0-9.]/g, '')) || 0;

  return (
    <div className="rounded-3xl border-2 border-ink/10 bg-white p-6 sm:p-8">
      <div className="flex items-center justify-between mb-6 pb-5 border-b border-ink/10">
        <h2 className="text-lg font-extrabold text-ink">Secure checkout</h2>
        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-ink-soft">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          256-bit encryption
        </span>
      </div>

      <ul className="space-y-3 mb-5">
        <li className="flex items-center justify-between text-sm text-ink">
          <span>{plan.name} plan</span>
          <span className="font-semibold tabular-nums">{fmt(planAmount)}</span>
        </li>
        {addOnCents > 0 && (
          <li className="flex items-center justify-between text-sm text-ink">
            <span>1:1 session add-on</span>
            <span className="font-semibold tabular-nums">
              {fmt(addOnCents / 100)}
            </span>
          </li>
        )}
      </ul>

      <hr className="border-ink/10 mb-5" />

      <div className="flex items-baseline justify-between mb-1">
        <span className="text-base font-bold text-ink">Total today</span>
        <span className="text-3xl font-extrabold text-ink tabular-nums">
          {fmt(totalDollars)}
        </span>
      </div>
      <p className="text-xs text-ink-soft mb-7">
        {plan.id === 'monthly'
          ? 'Then $29.99 each month. Cancel anytime.'
          : plan.id === 'quarterly'
            ? 'Then $59.97 every 3 months. Cancel anytime.'
            : 'Then $119.88 once a year. Cancel anytime.'}
      </p>

      {error && (
        <p className="text-sm text-err mb-3" role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        className="btn-primary"
        disabled={submitting}
        onClick={onPlaceholderConfirm}
      >
        {submitting ? 'Processing…' : `Complete checkout — ${fmt(totalDollars)}`}
      </button>

      <div className="mt-6 pt-5 border-t border-ink/10">
        <ul className="grid grid-cols-2 gap-y-2 gap-x-4 text-xs text-ink-soft">
          <li className="flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-shrink-0">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>30-day money-back</span>
          </li>
          <li className="flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-shrink-0">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Cancel anytime</span>
          </li>
          <li className="flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-shrink-0">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>PCI-DSS compliant</span>
          </li>
          <li className="flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0d9d7c" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="flex-shrink-0">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>Powered by Stripe</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ---------- Embedded Stripe (production with VITE_STRIPE_PUBLISHABLE_KEY) ----------

function EmbeddedPanel({ plan, addOnCents, addOnFlavor, clientSessionId, checkoutEndpoint }: Props) {
  const endpoint = checkoutEndpoint ?? '/api/create-checkout';
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    let alive = true;
    (async () => {
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            plan: plan.id,
            add_on: addOnFlavor,
            add_on_amount_cents: addOnCents || null,
            client_session_id: clientSessionId ?? undefined,
          }),
        });
        const data = (await res.json()) as { clientSecret?: string; error?: string };
        if (!alive) return;
        if (!res.ok || !data.clientSecret) {
          setError(data.error ?? 'Could not start checkout.');
          return;
        }
        setClientSecret(data.clientSecret);
      } catch (err) {
        if (alive) setError(err instanceof Error ? err.message : 'Network error');
      }
    })();
    return () => {
      alive = false;
    };
  }, [plan.id, addOnCents, addOnFlavor]);

  if (error) {
    return (
      <div className="rounded-3xl border-2 border-err/30 bg-err/5 p-6 text-sm text-ink-soft">
        {error}{' '}
        <button
          type="button"
          className="underline font-semibold text-ink"
          onClick={() => {
            fetchedRef.current = false;
            setError(null);
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="rounded-3xl border-2 border-ink/10 bg-canvas-tile p-10 text-center">
        <div className="inline-block w-7 h-7 border-2 border-ink/20 border-t-brand rounded-full animate-spin" />
        <p className="text-sm text-ink-soft mt-4">Loading secure checkout…</p>
      </div>
    );
  }

  return (
    <div className="rounded-3xl overflow-hidden border-2 border-ink/10 bg-white">
      <EmbeddedCheckoutProvider stripe={getStripe()} options={{ clientSecret }}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
